import { useContext } from "react";
import type { backendReportCashRegisterRowDTO } from "../../backend/target/typescript/dto";
import { RequestContext } from "../request-context";
import { requestGet, requireResult } from "../utils/network";
import { asNumber } from "../utils/strings";
import { getPeriodRange } from "../widget/i-period";
import { Modes } from "../widget/io-abstract";
import IONumber from "../widget/io-number";
import SearchPanel, { getPref, type FormValues } from "../widget/search-panel";
import TablePanel from "../widget/table-panel";

type ServerResponse = backendReportCashRegisterRowDTO[];

export default function ReportCashRegister(): React.ReactNode {
  const requestContext = useContext(RequestContext)!;

  const onSearch = (formData: FormValues): Promise<React.ReactNode> =>
    requestContext
      .fetchJSONAndMessage<ServerResponse>(
        "Generating report",
        requestGet("/api/reports/cash-register", {
          ...formData,
          ...getPeriodRange("year", "", "", formData["year"])
        })
      )
      .then(requireResult)
      .then((list) => (
        <TablePanel
          rows={list}
          headers={
            <thead>
              <tr>
                <th colSpan={2}>Period</th>
                <th colSpan={4}>Complete</th>
                <th colSpan={4}>Child</th>
                <th colSpan={4}>Poor and Child</th>
              </tr>
              <tr>
                <th>Year</th>
                <th>Month</th>

                <th>Total</th>
                <th>Asked</th>
                <th>Paid</th>
                <th>Unpaid</th>

                <th>Total</th>
                <th>Asked</th>
                <th>Paid</th>
                <th>Unpaid</th>

                <th>Total</th>
                <th>Asked</th>
                <th>Paid</th>
                <th>Unpaid</th>
              </tr>
            </thead>
          }
          lineColumns={(row) => {
            return (
              <>
                <td>{row.year}</td>
                <td>{row.month}</td>
                <td>{row.total_real}</td>
                <td>{row.total_asked}</td>
                <td>{row.paid}</td>
                <td>{asNumber(row.total_real) - asNumber(row.paid)}</td>
                <td>{row.child_total_real}</td>
                <td>{row.child_total_asked}</td>
                <td>{row.child_paid}</td>
                <td>
                  {asNumber(row.child_total_real) - asNumber(row.child_paid)}
                </td>
                <td>{row.poor_child_total_real}</td>
                <td>{row.poor_child_total_asked}</td>
                <td>{row.poor_child_paid}</td>
                <td>
                  {asNumber(row.poor_child_total_real) -
                    asNumber(row.poor_child_paid)}
                </td>
              </>
            );
          }}
          footers={
            <tfoot>
              <tr>
                <th colSpan={2}>total</th>
                <th>
                  {list.reduce((acc, row) => acc + asNumber(row.total_real), 0)}
                </th>
                <th>
                  {list.reduce(
                    (acc, row) => acc + asNumber(row.total_asked),
                    0
                  )}
                </th>
                <th>
                  {list.reduce((acc, row) => acc + asNumber(row.paid), 0)}
                </th>
                <th>
                  {list.reduce(
                    (acc, row) =>
                      acc + asNumber(row.total_real) - asNumber(row.paid),
                    0
                  )}
                </th>
                <th>
                  {list.reduce(
                    (acc, row) => acc + asNumber(row.child_total_real),
                    0
                  )}
                </th>
                <th>
                  {list.reduce(
                    (acc, row) => acc + asNumber(row.child_total_asked),
                    0
                  )}
                </th>
                <th>
                  {list.reduce((acc, row) => acc + asNumber(row.child_paid), 0)}
                </th>
                <th>
                  {list.reduce(
                    (acc, row) =>
                      acc +
                      asNumber(row.child_total_real) -
                      asNumber(row.child_paid),
                    0
                  )}
                </th>
                <th>
                  {list.reduce(
                    (acc, row) => acc + asNumber(row.poor_child_total_real),
                    0
                  )}
                </th>
                <th>
                  {list.reduce(
                    (acc, row) => acc + asNumber(row.poor_child_total_asked),
                    0
                  )}
                </th>
                <th>
                  {list.reduce(
                    (acc, row) => acc + asNumber(row.poor_child_paid),
                    0
                  )}
                </th>
                <th>
                  {list.reduce(
                    (acc, row) =>
                      acc +
                      asNumber(row.poor_child_total_real) -
                      asNumber(row.poor_child_paid),
                    0
                  )}
                </th>
              </tr>
            </tfoot>
          }

          reportBasename={`cash-register ${formData["year"]}`}
        />
      ));

  return (
    <div>
      <h1>
        <img src="/static/img/reports.gif" />
        Cash Register Report
      </h1>

      <SearchPanel
        form={
          <>
            <IONumber
              name="year"
              mode={Modes.input}
              value={Number(getPref("year", "" + new Date().getFullYear()))}
              required
              htmlProps={{ min: 1990, max: 2100 }}
            />
          </>
        }
        onSearch={onSearch}
      />
    </div>
  );
}
